import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { 
  FiDollarSign, 
  FiShoppingBag, 
  FiRefreshCw,
  FiDownload,
  FiFilter,
  FiCalendar,
  FiChevronDown,
  FiSearch,
  FiArrowUp,
  FiArrowDown,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiEye
} from 'react-icons/fi';
import { BsWallet2 } from 'react-icons/bs';
import { Helmet } from 'react-helmet-async';
import { fetchTransactions } from '../store/slices/paymentSlice';
import { format } from 'date-fns';
import { GlassCard } from '../components/ui/GlassCard';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export const TransactionsPage = () => {
  const dispatch = useDispatch();
  const { transactions, isLoading } = useSelector((state) => state.payments);
  const { user } = useSelector((state) => state.auth);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    dispatch(fetchTransactions());
  }, [dispatch]);

  useEffect(() => {
    if (transactions) {
      let filtered = [...transactions];

      // Apply search
      if (searchTerm) {
        filtered = filtered.filter(t => 
          t.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t.type?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // Apply type filter
      if (typeFilter !== 'all') {
        filtered = filtered.filter(t => t.type === typeFilter);
      }

      // Apply status filter
      if (statusFilter !== 'all') {
        filtered = filtered.filter(t => t.status === statusFilter);
      }

      // Apply date range
      if (dateRange.start) {
        filtered = filtered.filter(t => new Date(t.createdAt) >= new Date(dateRange.start));
      }
      if (dateRange.end) {
        filtered = filtered.filter(t => new Date(t.createdAt) <= new Date(dateRange.end));
      }

      // Apply sorting
      filtered.sort((a, b) => {
        switch (sortBy) {
          case 'newest':
            return new Date(b.createdAt) - new Date(a.createdAt);
          case 'oldest':
            return new Date(a.createdAt) - new Date(b.createdAt);
          case 'amount_high':
            return b.amount - a.amount;
          case 'amount_low':
            return a.amount - b.amount;
          default:
            return 0;
        }
      });

      setFilteredTransactions(filtered);
    }
  }, [transactions, searchTerm, typeFilter, statusFilter, dateRange, sortBy]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <FiCheckCircle className="w-5 h-5 text-green-500" />;
      case 'pending':
      case 'awaiting_confirmation':
        return <FiClock className="w-5 h-5 text-yellow-500" />;
      case 'failed':
      case 'rejected':
        return <FiXCircle className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'bg-green-100 text-green-700';
      case 'pending':
      case 'awaiting_confirmation':
        return 'bg-yellow-100 text-yellow-700';
      case 'failed':
      case 'rejected':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-luxury-100 text-luxury-700';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'deposit':
        return <FiArrowDown className="w-4 h-4 text-green-500" />;
      case 'purchase':
        return <FiShoppingBag className="w-4 h-4 text-blue-500" />;
      case 'refund':
        return <FiRefreshCw className="w-4 h-4 text-orange-500" />;
      default:
        return <FiDollarSign className="w-4 h-4 text-luxury-500" />;
    }
  };

  const getMetadataFields = (metadata) => {
    if (!metadata) return [];
    const raw = metadata.korapayData || metadata;
    const fields = [];

    if (typeof raw !== 'object' || raw === null) {
      return metadata.korapayReference ? [{ label: 'Korapay Reference', value: metadata.korapayReference }] : [];
    }

    if (metadata.korapayReference) {
      fields.push({ label: 'Korapay Reference', value: metadata.korapayReference });
    }
    if (raw.reference) {
      fields.push({ label: 'Gateway Reference', value: raw.reference });
    }
    if (raw.status) {
      fields.push({ label: 'Gateway Status', value: raw.status });
    }
    if (raw.description) {
      fields.push({ label: 'Description', value: raw.description });
    }
    if (raw.amount !== undefined) {
      fields.push({ label: 'Amount', value: `₦${Number(raw.amount).toLocaleString('en-NG', {minimumFractionDigits: 2})}` });
    }
    if (raw.amount_paid !== undefined) {
      fields.push({ label: 'Amount Paid', value: `₦${Number(raw.amount_paid).toLocaleString('en-NG', {minimumFractionDigits: 2})}` });
    }
    if (raw.fee !== undefined) {
      fields.push({ label: 'Fee', value: `₦${Number(raw.fee).toLocaleString('en-NG', {minimumFractionDigits: 2})}` });
    }
    if (raw.currency) {
      fields.push({ label: 'Currency', value: raw.currency });
    }
    if (raw.customer?.name) {
      fields.push({ label: 'Customer Name', value: raw.customer.name });
    }
    if (raw.customer?.email) {
      fields.push({ label: 'Customer Email', value: raw.customer.email });
    }
    if (raw.card) {
      if (raw.card.card_type) {
        fields.push({ label: 'Card Type', value: raw.card.card_type });
      }
      if (raw.card.first_six || raw.card.last_four) {
        fields.push({ label: 'Card Number', value: `${raw.card.first_six || '••••••'} •••• ${raw.card.last_four || '••••'}` });
      }
      if (raw.card.expiry) {
        fields.push({ label: 'Card Expiry', value: raw.card.expiry });
      }
    }

    return fields;
  };

  // Pre-compute metadata fields for the currently selected transaction (if any)
  const computedMetadataFields = selectedTransaction ? getMetadataFields(selectedTransaction.metadata) : [];

  const calculateTotals = () => {
    const totals = {
      totalDeposits: 0,
      totalPurchases: 0,
      totalRefunds: 0,
      pendingTransactions: 0
    };

    transactions?.forEach(t => {
      if (t.status === 'success') {
        if (t.type === 'deposit') totals.totalDeposits += t.amount;
        if (t.type === 'purchase') totals.totalPurchases += t.amount;
        if (t.type === 'refund') totals.totalRefunds += t.amount;
      }
      if (t.status === 'pending') totals.pendingTransactions++;
    });

    return totals;
  };

  const exportToExcel = () => {
    const data = filteredTransactions.map(t => ({
      Reference: t.reference,
      Type: t.type,
      Amount: `₦${t.amount.toLocaleString()}`,
      Status: t.status,
      Date: format(new Date(t.createdAt), 'MMM dd, yyyy HH:mm'),
      'Payment Method': t.paymentMethod || 'N/A'
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Transactions');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, `transactions_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  const totals = calculateTotals();

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Transactions | Nitro Logs</title>
      </Helmet>

      <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-luxury-50 via-white to-luxury-100">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="mb-8"
          >
            <h1 className="text-3xl font-display font-bold text-luxury-900 mb-2">
              Transaction <span className="gradient-text">History</span>
            </h1>
            <p className="text-luxury-600">
              Track all your deposits, purchases, and refunds
            </p>
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 mb-8"
          >
            <motion.div variants={fadeInUp}>
              <GlassCard className="p-3 sm:p-4">
                <div className="flex items-center justify-between mb-2">
                  <FiArrowDown className="w-4 sm:w-5 h-4 sm:h-5 text-green-500" />
                  <span className="text-xs text-luxury-500">Total Deposits</span>
                </div>
                <span className="text-lg sm:text-xl font-bold gradient-text">
                  ₦{totals.totalDeposits.toLocaleString()}
                </span>
              </GlassCard>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <GlassCard className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <FiShoppingBag className="w-5 h-5 text-blue-500" />
                  <span className="text-xs text-luxury-500">Total Purchases</span>
                </div>
                <span className="text-xl font-bold gradient-text">
                  ₦{totals.totalPurchases.toLocaleString()}
                </span>
              </GlassCard>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <GlassCard className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <FiRefreshCw className="w-5 h-5 text-orange-500" />
                  <span className="text-xs text-luxury-500">Total Refunds</span>
                </div>
                <span className="text-xl font-bold gradient-text">
                  ₦{totals.totalRefunds.toLocaleString()}
                </span>
              </GlassCard>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <GlassCard className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <FiClock className="w-5 h-5 text-yellow-500" />
                  <span className="text-xs text-luxury-500">Pending</span>
                </div>
                <span className="text-xl font-bold gradient-text">
                  {totals.pendingTransactions}
                </span>
              </GlassCard>
            </motion.div>
          </motion.div>

          {/* Current Balance Card */}
          <motion.div
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="mb-8"
          >
            <GlassCard className="p-6 bg-gradient-to-r from-primary-500 to-secondary-500 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 mb-1">Current Wallet Balance</p>
                  <h2 className="text-3xl font-bold">₦{user?.balance?.toLocaleString() || 0}</h2>
                </div>
                <BsWallet2 className="w-12 h-12 text-white/30" />
              </div>
            </GlassCard>
          </motion.div>

          {/* Filters Bar */}
          <motion.div
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="glass rounded-2xl p-4 mb-8"
          >
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-luxury-400" />
                <input
                  type="text"
                  placeholder="Search by reference..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-luxury pl-12 w-full"
                />
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-6 py-3 rounded-xl flex items-center space-x-2 transition-all duration-300 ${
                  showFilters 
                    ? 'bg-primary-600 text-white' 
                    : 'glass hover:bg-primary-50 text-luxury-700'
                }`}
              >
                <FiFilter className="w-5 h-5" />
                <span>Filters</span>
                <FiChevronDown className={`w-4 h-4 transition-transform duration-300 ${
                  showFilters ? 'rotate-180' : ''
                }`} />
              </button>

              {/* Export Button */}
              <button
                onClick={exportToExcel}
                className="px-6 py-3 glass rounded-xl flex items-center space-x-2 hover:bg-primary-50 transition-colors"
              >
                <FiDownload className="w-5 h-5" />
                <span>Export</span>
              </button>
            </div>

            {/* Filter Panel */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="pt-6 mt-4 border-t border-luxury-200">
                    <div className="grid md:grid-cols-4 gap-4">
                      {/* Date Range */}
                      <div>
                        <label className="block text-sm font-medium text-luxury-700 mb-2">
                          From
                        </label>
                        <input
                          type="date"
                          value={dateRange.start}
                          onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                          className="input-luxury"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-luxury-700 mb-2">
                          To
                        </label>
                        <input
                          type="date"
                          value={dateRange.end}
                          onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                          className="input-luxury"
                        />
                      </div>

                      {/* Type Filter */}
                      <div>
                        <label className="block text-sm font-medium text-luxury-700 mb-2">
                          Type
                        </label>
                        <select
                          value={typeFilter}
                          onChange={(e) => setTypeFilter(e.target.value)}
                          className="input-luxury"
                        >
                          <option value="all">All Types</option>
                          <option value="deposit">Deposit</option>
                          <option value="purchase">Purchase</option>
                          <option value="refund">Refund</option>
                        </select>
                      </div>

                      {/* Status Filter */}
                      <div>
                        <label className="block text-sm font-medium text-luxury-700 mb-2">
                          Status
                        </label>
                        <select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          className="input-luxury"
                        >
                          <option value="all">All Status</option>
                          <option value="success">Success</option>
                          <option value="pending">Pending</option>
                          <option value="awaiting_confirmation">Awaiting proof</option>
                          <option value="failed">Failed</option>
                        </select>
                      </div>

                      {/* Sort By */}
                      <div>
                        <label className="block text-sm font-medium text-luxury-700 mb-2">
                          Sort By
                        </label>
                        <select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                          className="input-luxury"
                        >
                          <option value="newest">Newest First</option>
                          <option value="oldest">Oldest First</option>
                          <option value="amount_high">Amount: High to Low</option>
                          <option value="amount_low">Amount: Low to High</option>
                        </select>
                      </div>
                    </div>

                    {/* Active Filters */}
                    <div className="flex flex-wrap gap-2 mt-4">
                      {typeFilter !== 'all' && (
                        <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm flex items-center">
                          Type: {typeFilter}
                          <button
                            onClick={() => setTypeFilter('all')}
                            className="ml-2 hover:text-primary-900"
                          >
                            ×
                          </button>
                        </span>
                      )}
                      {statusFilter !== 'all' && (
                        <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm flex items-center">
                          Status: {statusFilter}
                          <button
                            onClick={() => setStatusFilter('all')}
                            className="ml-2 hover:text-primary-900"
                          >
                            ×
                          </button>
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Transactions List */}
          {isLoading ? (
            <LoadingSkeleton count={5} />
          ) : (
            <motion.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="space-y-4"
            >
              {filteredTransactions.length > 0 ? (
                filteredTransactions.map((transaction) => (
                  <motion.div
                    key={transaction._id}
                    variants={fadeInUp}
                    whileHover={{ scale: 1.01 }}
                    className="glass rounded-2xl p-4 cursor-pointer hover:shadow-luxury-lg transition-all duration-300"
                    onClick={() => {
                      setSelectedTransaction(transaction);
                      setShowDetails(true);
                    }}
                  >
                    <div className="flex items-center justify-between gap-4 min-w-0">
                      <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
                        <div className={`w-10 h-10 flex-shrink-0 rounded-xl flex items-center justify-center ${
                          transaction.type === 'deposit' ? 'bg-green-100' :
                          transaction.type === 'purchase' ? 'bg-blue-100' : 'bg-orange-100'
                        }`}>
                          {getTypeIcon(transaction.type)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center space-x-2 min-w-0">
                            <h3 className="font-semibold text-luxury-900 capitalize text-sm sm:text-base flex-shrink-0">
                              {transaction.type}
                            </h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${getStatusColor(transaction.status)}`}>
                              {transaction.status}
                            </span>
                          </div>
                          <p className="text-xs sm:text-sm text-luxury-500 truncate">
                            {transaction.reference}
                            {transaction.paymentMethod === 'manual' && (
                              <span className="ml-2 text-[10px] px-1 py-0.5 bg-yellow-200 text-yellow-800 rounded">manual</span>
                            )}
                          </p>
                          <p className="text-xs text-luxury-400">
                            {format(new Date(transaction.createdAt), 'MMM dd, yyyy • h:mm a')}
                          </p>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        {transaction.paymentMethod === 'manual' && transaction.proofUrl && (
                          <a href={`${(import.meta.env.VITE_API_URL || 'http://localhost:4000').replace(/\/api$/,'')}/${transaction.proofUrl}`} target="_blank" rel="noreferrer" className="block text-xs text-primary-500 underline mb-1">
                            View proof
                          </a>
                        )}
                        <p className={`text-lg sm:text-xl font-bold ${
                          transaction.type === 'deposit' ? 'text-green-600' :
                          transaction.type === 'purchase' ? 'text-blue-600' : 'text-orange-600'
                        }`}>
                          {transaction.type === 'purchase' ? '-' : '+'} ₦{transaction.amount.toLocaleString()}
                        </p>
                        <p className="text-xs text-luxury-400">
                          {transaction.paymentMethod || 'Wallet'}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))
              ) : (
                <motion.div
                  variants={fadeInUp}
                  className="text-center py-20"
                >
                  <div className="w-24 h-24 mx-auto mb-6 bg-luxury-200 rounded-full flex items-center justify-center">
                    <FiDollarSign className="w-8 h-8 text-luxury-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-luxury-900 mb-2">
                    No Transactions Found
                  </h3>
                  <p className="text-luxury-600">
                    Your transaction history will appear here
                  </p>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Transaction Details Modal */}
          <AnimatePresence>
            {showDetails && selectedTransaction && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                onClick={() => setShowDetails(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="glass max-w-lg w-full rounded-2xl p-6 max-h-[80vh] overflow-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h2 className="text-xl font-semibold text-luxury-900 mb-4">
                    Transaction Details
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-4 border-b border-luxury-200">
                      <span className="text-luxury-600">Reference</span>
                      <span className="font-mono text-sm text-luxury-900 truncate">{selectedTransaction.reference}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-luxury-600">Type</span>
                      <span className="capitalize font-medium text-luxury-900">{selectedTransaction.type}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-luxury-600">Amount</span>
                      <span className={`font-bold ${
                        selectedTransaction.type === 'deposit' ? 'text-green-600' :
                        selectedTransaction.type === 'purchase' ? 'text-blue-600' : 'text-orange-600'
                      }`}>
                        ₦{selectedTransaction.amount.toLocaleString()}
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-luxury-600">Status</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedTransaction.status)}`}>
                        {selectedTransaction.status}
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-luxury-600">Date</span>
                      <span className="text-luxury-900">
                        {format(new Date(selectedTransaction.createdAt), 'MMMM dd, yyyy')}
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-luxury-600">Time</span>
                      <span className="text-luxury-900">
                        {format(new Date(selectedTransaction.createdAt), 'h:mm:ss a')}
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-luxury-600">Payment Method</span>
                      <span className="capitalize text-luxury-900">{selectedTransaction.paymentMethod || 'Wallet'}</span>
                    </div>

                    {selectedTransaction.virtualNumberPurchase && (
                      <div className="pt-4 border-t border-luxury-200">
                        <p className="text-sm text-luxury-600 mb-2">Virtual Number Details</p>
                        <div className="space-y-2 text-sm text-luxury-900">
                          {selectedTransaction.virtualNumberPurchase.serviceName && (
                            <div className="flex justify-between">
                              <span>Service</span>
                              <span>{selectedTransaction.virtualNumberPurchase.serviceName}</span>
                            </div>
                          )}
                          {selectedTransaction.virtualNumberPurchase.countryName && (
                            <div className="flex justify-between">
                              <span>Country</span>
                              <span>{selectedTransaction.virtualNumberPurchase.countryName}</span>
                            </div>
                          )}
                          {selectedTransaction.virtualNumberPurchase.phoneNumber && (
                            <div className="flex justify-between">
                              <span>Phone</span>
                              <span>{selectedTransaction.virtualNumberPurchase.phoneNumber}</span>
                            </div>
                          )}
                          {selectedTransaction.virtualNumberPurchase.status && (
                            <div className="flex justify-between">
                              <span>Status</span>
                              <span className="capitalize">{selectedTransaction.virtualNumberPurchase.status}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {selectedTransaction.metadata && (
                      <div className="pt-4 border-t border-luxury-200">
                        <p className="text-sm text-luxury-600 mb-2">Additional Information</p>
                        <div className="space-y-3">
                          {selectedTransaction.metadata.serviceName && (
                            <div className="flex justify-between text-sm text-luxury-900">
                              <span>Service</span>
                              <span>{selectedTransaction.metadata.serviceName}</span>
                            </div>
                          )}
                          {selectedTransaction.metadata.country && (
                            <div className="flex justify-between text-sm text-luxury-900">
                              <span>Country</span>
                              <span>{selectedTransaction.metadata.country}</span>
                            </div>
                          )}
                          {selectedTransaction.metadata.phoneNumber && (
                            <div className="flex justify-between text-sm text-luxury-900">
                              <span>Phone Number</span>
                              <span>{selectedTransaction.metadata.phoneNumber}</span>
                            </div>
                          )}
                          {selectedTransaction.metadata.codeReceived !== undefined && (
                            <div className="flex justify-between text-sm text-luxury-900">
                              <span>Code Received</span>
                              <span>{selectedTransaction.metadata.codeReceived ? 'Yes' : 'No'}</span>
                            </div>
                          )}
                          {selectedTransaction.metadata.activationStartTime && (
                            <div className="flex justify-between text-sm text-luxury-900">
                              <span>Activation Starts</span>
                              <span>{format(new Date(selectedTransaction.metadata.activationStartTime), 'MMM dd, yyyy h:mm a')}</span>
                            </div>
                          )}
                          {selectedTransaction.metadata.activationEndTime && (
                            <div className="flex justify-between text-sm text-luxury-900">
                              <span>Activation Ends</span>
                              <span>{format(new Date(selectedTransaction.metadata.activationEndTime), 'MMM dd, yyyy h:mm a')}</span>
                            </div>
                          )}
                          {selectedTransaction.metadata.expirationMinutes !== undefined && (
                            <div className="flex justify-between text-sm text-luxury-900">
                              <span>TTL</span>
                              <span>{selectedTransaction.metadata.expirationMinutes} mins</span>
                            </div>
                          )}
                          {selectedTransaction.metadata.reason && (
                            <div className="flex justify-between text-sm text-luxury-900">
                              <span>Reason</span>
                              <span>{selectedTransaction.metadata.reason.replace('_', ' ')}</span>
                            </div>
                          )}
                          {computedMetadataFields.map((field) => (
                            <div key={field.label} className="flex justify-between text-sm text-luxury-900">
                              <span>{field.label}</span>
                              {field.link ? (
                                <a
                                  href={field.value}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-blue-600 hover:underline text-right"
                                >
                                  {field.value}
                                </a>
                              ) : (
                                <span className="font-medium text-right">{field.value}</span>
                              )}
                            </div>
                          ))}

                          {computedMetadataFields.length === 0 && !selectedTransaction.metadata.serviceName && !selectedTransaction.metadata.country && !selectedTransaction.metadata.phoneNumber && !selectedTransaction.metadata.codeReceived && !selectedTransaction.metadata.activationStartTime && !selectedTransaction.metadata.activationEndTime && selectedTransaction.metadata && (
                            <pre className="text-xs bg-luxury-100 p-3 rounded-lg overflow-auto whitespace-pre-wrap">
                              {JSON.stringify(selectedTransaction.metadata, null, 2)}
                            </pre>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-end mt-6">
                    <button
                      onClick={() => setShowDetails(false)}
                      className="px-6 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
};